import {
  Button,
  ManageableItemsList,
  Modal,
  ModalProps,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';

import { TFolderEntry } from '@/api/queries/project/useGetProjectFolderEntries';
import { findTypeAndIconFromMimeType } from '@/helpers/fileHelper';

import UntitledIcon from '@payaca/untitled-icons';

interface IProps extends Omit<ModalProps, 'title'> {
  title?: string;
  // Is loading files
  isLoading?: boolean;
  files: TFolderEntry[];
  onSubmit?: (files: TFolderEntry[]) => void;
  // Is processing submission
  isProcessing?: boolean;
  disabledFiles?: {
    uploadIds: string[];
    folderEntryIds: string[];
  };
  multiple?: boolean;
  count: number;
  totalCount: number;
  onLoadMore: () => void;
  hasNextPage: boolean;
}

const LinkFilesModal: FC<IProps> = (props) => {
  const {
    onSubmit,
    isLoading = false,
    isProcessing = false,
    files,
    disabledFiles = { uploadIds: [], folderEntryIds: [] },
    multiple = true,
    // Modal props
    title = 'Link files',
    size = 'md',
    count,
    totalCount,
    onLoadMore,
    hasNextPage,
    ...rest
  } = props;

  const [selectedUploads, setSelectedUploads] = useState<TFolderEntry[]>([]);

  return (
    <Modal title={title} size="md" {...rest}>
      <Modal.Body>
        <div>
          <ManageableItemsList>
            <ManageableItemsList.Table<TFolderEntry>
              items={files}
              uniqueKey="id"
              isLoading={isLoading}
              enableSelectAll={multiple}
              selectedItems={selectedUploads}
              setSelectedItems={(newState) => {
                if (multiple) {
                  setSelectedUploads(newState);
                } else {
                  setSelectedUploads((prev) => {
                    return newState.filter(
                      (i) => !prev.find((x) => x.id === i.id)
                    );
                  });
                }
              }}
              shouldDisableItem={(item) => {
                if (
                  item.__typename !== 'UploadFolderEntry' &&
                  item.__typename !== 'DocumentInstanceFolderEntry'
                ) {
                  // Shouldn't get here
                  return {
                    disabled: true,
                    disabledMessage:
                      'Only uploads and document instances can be linked',
                  };
                }

                let disabled = false;
                if (item.__typename == 'UploadFolderEntry') {
                  disabled = !!disabledFiles.uploadIds.find(
                    (x) => x === item.upload.privateId
                  );
                }

                if (item.__typename == 'DocumentInstanceFolderEntry') {
                  disabled = !!disabledFiles.folderEntryIds.find(
                    (x) => x === item.id
                  );
                }

                return {
                  disabled,
                  disabledMessage: disabled
                    ? 'This upload is already linked'
                    : undefined,
                };
              }}
            >
              <ManageableItemsList.Table.Column<TFolderEntry, 'id'>
                header=""
                field="id"
                render={(id, row) => {
                  switch (row.__typename) {
                    case 'DocumentInstanceFolderEntry':
                      return (
                        <UntitledIcon
                          name={'file-04'}
                          className="h-auto w-12"
                        />
                      );
                    case 'UploadFolderEntry':
                      if (!row.upload.thumbnailUrl) {
                        return (
                          <UntitledIcon
                            name={
                              findTypeAndIconFromMimeType(
                                row.upload.mimeType
                              )[1]
                            }
                            className="h-7 w-7"
                          />
                        );
                      }

                      return (
                        <img
                          className="h-auto w-12"
                          src={row.upload.thumbnailUrl}
                          alt="File preview"
                        />
                      );
                    default:
                      return null;
                  }
                }}
              />
              <ManageableItemsList.Table.Column<TFolderEntry, 'name'>
                header="Name"
                field="name"
                render={(fileName) => (
                  <span className="truncate">{fileName}</span>
                )}
              />
            </ManageableItemsList.Table>
            <ManageableItemsList.InfinitePaginationBar
              count={count}
              totalCount={totalCount}
              onLoadMore={onLoadMore}
              hasNextPage={hasNextPage}
            />
          </ManageableItemsList>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            disabled={!selectedUploads.length}
            onClick={() => onSubmit?.(selectedUploads)}
            isProcessing={isProcessing}
          >
            Link{selectedUploads.length ? ` ${selectedUploads.length}` : ``}{' '}
            files
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default LinkFilesModal;
